import { renderLucideIcon } from '../utils/lucide-icons.js'
import { renderPopin } from './popin-renderer.js'

function normalizeDebriefMatterIngestionUi(result) {
  if (!result || typeof result !== 'object') return null

  var ui = String(result.type || '').toLowerCase() === 'debrief-matter-ingestion'
    ? result
    : result.debriefMatterIngestionUi || result.matterIngestionUi || result.ui || null
  if (!ui || typeof ui !== 'object') return null
  if (String(ui.type || '').toLowerCase() !== 'debrief-matter-ingestion') return null

  var options = Array.isArray(ui.options) ? ui.options.map(normalizeDebriefMatterIngestionOption).filter(Boolean) : []
  if (!options.length) return null

  return {
    type: 'debrief-matter-ingestion',
    title: String(ui.title || 'Comment veux-tu fournir la matière du rendez-vous ?'),
    subtitle: ui.subtitle ? String(ui.subtitle) : '',
    activityLabel: ui.activityLabel ? String(ui.activityLabel) : '',
    intro: ui.intro ? String(ui.intro) : '',
    layout: String(ui.layout || '').toLowerCase() === 'tabbed-input' ? 'tabbed-input' : 'choice-grid',
    existingReviewAction: normalizeExistingReviewAction(ui.existingReviewAction),
    options: options
  }
}

function normalizeDebriefMatterIngestionOption(option) {
  if (!option || typeof option !== 'object') return null
  var id = String(option.id || '').trim()
  var label = String(option.label || '').trim()
  if (!id || !label) return null
  var enabled = option.enabled !== false
  var action = normalizeDebriefMatterIngestionAction(option.action)

  return {
    id: id,
    label: label,
    icon: String(option.icon || '').trim(),
    enabled: enabled,
    badge: option.badge ? String(option.badge) : '',
    action: enabled ? action : null
  }
}

function normalizeDebriefMatterIngestionAction(action) {
  if (!action || typeof action !== 'object') return null
  var type = String(action.type || '').toLowerCase()
  if (type === 'goto_step') {
    var stepKey = String(action.stepKey || '').trim()
    if (!stepKey) return null
    return {
      type: 'goto_step',
      stepKey: stepKey,
      requestContext: action.requestContext && typeof action.requestContext === 'object' ? action.requestContext : {}
    }
  }
  if (type === 'show_text_prompt') {
    var message = String(action.message || '').trim()
    if (!message) return null
    return {
      type: 'show_text_prompt',
      message: message
    }
  }
  if (type === 'open_audio_file') {
    return { type: 'open_audio_file' }
  }
  if (type === 'capture_text') {
    var sourceKind = String(action.sourceKind || '').toLowerCase()
    if (sourceKind !== 'dictation' && sourceKind !== 'transcript') sourceKind = 'notes'
    return {
      type: 'capture_text',
      sourceKind: sourceKind
    }
  }
  return null
}

function normalizeExistingReviewAction(value) {
  if (!value || typeof value !== 'object') return null
  var label = String(value.label || '').trim()
  var action = normalizeDebriefMatterIngestionAction(value.action)
  if (!label || !action) return null
  return {
    label: label,
    action: action
  }
}

function renderDebriefMatterIngestionUi(ui) {
  if (ui.layout === 'tabbed-input') return renderDebriefMatterTabbedInputUi(ui)

  var title = ui.title
    ? '<div class="wc-debrief-ingestion-title">' + escapeHtml(ui.title) + '</div>'
    : ''
  var options = ui.options.map(function (option, index) {
    var disabled = option.enabled && option.action ? '' : ' disabled aria-disabled="true"'
    var icon = option.icon ? renderLucideIcon(option.icon) : ''
    var badge = option.badge
      ? '<span class="wc-debrief-ingestion-badge">' + escapeHtml(option.badge) + '</span>'
      : ''

    return (
      '<button type="button" class="wc-debrief-ingestion-option" data-debrief-ingestion-option-index="' + String(index) + '"' + disabled + '>' +
        '<span class="wc-debrief-ingestion-icon">' + icon + '</span>' +
        '<span class="wc-debrief-ingestion-label">' + escapeHtml(option.label) + '</span>' +
        badge +
      '</button>'
    )
  }).join('')

  return (
    '<div class="wc-debrief-ingestion-ui">' +
      title +
      '<div class="wc-debrief-ingestion-options">' + options + '</div>' +
    '</div>'
  )
}

function renderDebriefMatterTabbedInputUi(ui) {
  var pasteOption = findOption(ui, 'paste')
  var voiceOption = findOption(ui, 'voice')
  var options = ui.options.map(function (option, index) {
    var selected = option.id === 'paste'
    var disabled = option.enabled && option.action ? '' : ' disabled aria-disabled="true"'
    var controls = 'wc-debrief-ingestion-panel-' + escapeAttributeValue(option.id)
    var badge = option.badge ? '<span class="wc-debrief-ingestion-tab-badge">*</span>' : ''
    return (
      '<button type="button" class="wc-debrief-ingestion-tab' + (selected ? ' active' : '') + '" role="tab" aria-selected="' + String(selected) + '" aria-controls="' + controls + '" data-debrief-ingestion-tab="' + escapeAttributeValue(option.id) + '" data-debrief-ingestion-option-index="' + String(index) + '"' + disabled + '>' +
        '<span class="wc-debrief-ingestion-tab-icon">' + (option.icon ? renderLucideIcon(option.icon) : '') + '</span>' +
        '<span>' + escapeHtml(option.label) + badge + '</span>' +
      '</button>'
    )
  }).join('')

  var activity = ui.activityLabel
    ? '<div class="wc-debrief-ingestion-meeting">' + escapeHtml(ui.activityLabel) + '</div>'
    : ''
  var intro = ui.intro
    ? '<p class="wc-debrief-ingestion-intro">' + escapeHtml(ui.intro) + '</p>'
    : ''
  var footnote = ui.options.some(function (option) { return option.badge })
    ? '<div class="wc-debrief-ingestion-footnote">* Disponible prochainement.</div>'
    : ''

  return (
    '<div class="wc-debrief-ingestion-ui wc-debrief-ingestion-tabbed">' +
      '<div class="wc-debrief-ingestion-title">' + escapeHtml(ui.title) + '</div>' +
      activity +
      intro +
      '<div class="wc-debrief-ingestion-tabs" role="tablist" aria-label="Mode de transmission">' + options + '</div>' +
      renderPastePanel(pasteOption, ui.existingReviewAction) +
      renderVoicePanel(voiceOption, ui.existingReviewAction) +
      footnote +
    '</div>'
  )
}

function renderPastePanel(option, existingReviewAction) {
  var action = option && option.action && option.action.type === 'capture_text' ? option.action : null
  var sourceKind = action ? action.sourceKind : 'notes'
  return (
    '<section class="wc-debrief-ingestion-panel active" id="wc-debrief-ingestion-panel-paste" role="tabpanel" data-debrief-ingestion-panel="paste">' +
      '<textarea class="wc-debrief-ingestion-textarea" data-debrief-ingestion-text="paste" placeholder="Colle ici tes notes de rendez-vous ou une transcription..."></textarea>' +
      '<div class="wc-debrief-ingestion-actions">' +
        renderExistingReviewButton(existingReviewAction) +
        '<button type="button" class="wc-debrief-ingestion-primary" data-debrief-ingestion-submit="paste" data-debrief-source-kind="' + escapeAttributeValue(sourceKind) + '" disabled>Générer le débrief</button>' +
      '</div>' +
    '</section>'
  )
}

function renderVoicePanel(option, existingReviewAction) {
  var action = option && option.action && option.action.type === 'capture_text' ? option.action : null
  var sourceKind = action ? action.sourceKind : 'dictation'
  return (
    '<section class="wc-debrief-ingestion-panel" id="wc-debrief-ingestion-panel-voice" role="tabpanel" data-debrief-ingestion-panel="voice">' +
      '<div class="wc-debrief-voice-grid">' +
        '<div class="wc-debrief-voice-box wc-debrief-voice-card">' +
          '<button type="button" class="wc-debrief-voice-mic" data-debrief-voice-toggle aria-label="Commencer la dictée" title="Commencer la dictée">' + renderLucideIcon('mic') + '</button>' +
          '<strong>Dicter maintenant</strong>' +
          '<p>Raconte librement les échanges, les décisions et les prochaines étapes.</p>' +
          '<p class="wc-debrief-voice-status" data-debrief-voice-status role="status">Appuie sur le micro pour commencer la dictée.</p>' +
          '<textarea class="wc-debrief-ingestion-textarea wc-debrief-voice-transcript" data-debrief-ingestion-text="voice" placeholder="Ta dictée apparaîtra ici. Tu pourras aussi la corriger..."></textarea>' +
          '<div class="wc-debrief-ingestion-actions">' +
            renderExistingReviewButton(existingReviewAction) +
            '<button type="button" class="wc-debrief-ingestion-primary" data-debrief-ingestion-submit="voice" data-debrief-source-kind="' + escapeAttributeValue(sourceKind) + '" disabled>Générer le débrief</button>' +
          '</div>' +
        '</div>' +
      '</div>' +
        '</div>' +
      '</div>' +
    '</section>'
  )
}

function renderExistingReviewButton(existingReviewAction) {
  return existingReviewAction
    ? '<button type="button" class="wc-debrief-ingestion-secondary" data-debrief-existing-review>' + escapeHtml(existingReviewAction.label) + '</button>'
    : ''
}

function bindDebriefMatterIngestionUi(el, ui, handlers) {
  if (!el || !ui || !handlers) return
  if (ui.layout === 'tabbed-input') {
    bindDebriefMatterTabbedInputUi(el, ui, handlers)
    return
  }
  Array.prototype.slice.call(el.querySelectorAll('[data-debrief-ingestion-option-index]')).forEach(function (button) {
    button.addEventListener('click', function () {
      if (button.disabled) return
      var index = Number(button.getAttribute('data-debrief-ingestion-option-index') || -1)
      var option = ui.options[index]
      if (!option || !option.action) return

      if (option.action.type === 'open_audio_file' && typeof handlers.onAudioFile === 'function') {
        handlers.onAudioFile(option)
        return
      }
      if (option.action.type === 'show_text_prompt' && typeof handlers.onTextPrompt === 'function') {
        button.disabled = true
        handlers.onTextPrompt(option)
        return
      }
      if (typeof handlers.onAction === 'function') {
        button.disabled = true
        handlers.onAction(option)
      }
    })
  })
}

function bindDebriefMatterTabbedInputUi(el, ui, handlers) {
  var recognition = null
  var recording = false
  var committed = ''
  var SpeechRecognition = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null

  function setActiveTab(id) {
    Array.prototype.slice.call(el.querySelectorAll('[data-debrief-ingestion-tab]')).forEach(function (tab) {
      var selected = tab.getAttribute('data-debrief-ingestion-tab') === id
      tab.classList.toggle('active', selected)
      tab.setAttribute('aria-selected', String(selected))
    })
    Array.prototype.slice.call(el.querySelectorAll('[data-debrief-ingestion-panel]')).forEach(function (panel) {
      panel.classList.toggle('active', panel.getAttribute('data-debrief-ingestion-panel') === id)
    })
  }

  function updateSubmit(mode) {
    var textarea = el.querySelector('[data-debrief-ingestion-text="' + mode + '"]')
    var submit = el.querySelector('[data-debrief-ingestion-submit="' + mode + '"]')
    var audioFile = mode === 'audio' ? resolveVoiceFile() : null
    if (submit) submit.disabled = !(textarea && textarea.value.trim()) && !audioFile
  }

  function submitMatter(mode) {
    var textarea = el.querySelector('[data-debrief-ingestion-text="' + mode + '"]')
    var submit = el.querySelector('[data-debrief-ingestion-submit="' + mode + '"]')
    var text = textarea && textarea.value ? textarea.value.trim() : ''
    var audioFile = mode === 'audio' ? resolveVoiceFile() : null
    if ((!text && !audioFile) || !submit) return
    submit.disabled = true
    if (typeof handlers.onSubmitMatter === 'function') {
      handlers.onSubmitMatter({
        method: mode,
        sourceKind: submit.getAttribute('data-debrief-source-kind') || (mode === 'voice' ? 'dictation' : 'notes'),
        text: text,
        audioFile: audioFile
      })
    }
  }

  function setVoiceStatus(message) {
    var status = el.querySelector('[data-debrief-voice-status]')
    if (status) status.textContent = message
  }

  function updateVoiceButton(button) {
    if (!button) return
    button.classList.toggle('recording', recording)
    button.setAttribute('aria-label', recording ? 'Arrêter la dictée' : 'Commencer la dictée')
    button.setAttribute('title', recording ? 'Arrêter la dictée' : 'Commencer la dictée')
  }

  Array.prototype.slice.call(el.querySelectorAll('[data-debrief-ingestion-tab]')).forEach(function (tab) {
    tab.addEventListener('click', function () {
      if (tab.disabled) return
      setActiveTab(tab.getAttribute('data-debrief-ingestion-tab') || 'paste')
    })
  })

  Array.prototype.slice.call(el.querySelectorAll('[data-debrief-ingestion-text]')).forEach(function (textarea) {
    textarea.addEventListener('input', function () {
      var mode = textarea.getAttribute('data-debrief-ingestion-text') || 'paste'
      if (mode === 'voice') committed = textarea.value
      updateSubmit(mode)
    })
  })

  Array.prototype.slice.call(el.querySelectorAll('[data-debrief-ingestion-submit]')).forEach(function (button) {
    button.addEventListener('click', function () {
      submitMatter(button.getAttribute('data-debrief-ingestion-submit') || 'paste')
    })
  })

  Array.prototype.slice.call(el.querySelectorAll('[data-debrief-existing-review]')).forEach(function (button) {
    button.addEventListener('click', function () {
      if (!ui.existingReviewAction || !ui.existingReviewAction.action || typeof handlers.onAction !== 'function') return
      button.disabled = true
      handlers.onAction(ui.existingReviewAction)
    })
  })

  var voiceFileInput = el.querySelector('[data-debrief-voice-file]')
  var voiceFileName = el.querySelector('[data-debrief-voice-file-name]')
  if (voiceFileInput && voiceFileName) {
    voiceFileInput.addEventListener('change', function () {
      var file = resolveVoiceFile()
      voiceFileName.textContent = file && file.name ? file.name : 'Aucun fichier sélectionné'
      updateSubmit('audio')
    })
  }

  var micButton = el.querySelector('[data-debrief-voice-toggle]')
  var voiceTextarea = el.querySelector('[data-debrief-ingestion-text="voice"]')
  if (micButton) {
    micButton.addEventListener('click', function () {
      if (recording) {
        if (recognition) recognition.stop()
        recording = false
        updateVoiceButton(micButton)
        setVoiceStatus('Dictée arrêtée. Tu peux relire et corriger ton texte.')
        return
      }
      if (!SpeechRecognition || !voiceTextarea) {
        setVoiceStatus('La dictée vocale n’est pas prise en charge par ce navigateur. Tu peux saisir ton texte ci-dessous.')
        if (voiceTextarea) voiceTextarea.focus()
        return
      }

      recognition = new SpeechRecognition()
      recognition.lang = handlers.speechLang || 'fr-FR'
      recognition.continuous = true
      recognition.interimResults = true
      committed = voiceTextarea.value.trim()
      recognition.onresult = function (event) {
        var interim = ''
        for (var i = event.resultIndex; i < event.results.length; i++) {
          var chunk = event.results[i][0].transcript
          if (event.results[i].isFinal) committed += (committed ? ' ' : '') + chunk.trim()
          else interim += chunk
        }
        voiceTextarea.value = committed + (interim ? ' ' + interim : '')
        updateSubmit('voice')
      }
      recognition.onerror = function (event) {
        setVoiceStatus(event && event.error === 'not-allowed'
          ? 'Accès au microphone refusé. Autorise-le dans ton navigateur pour dicter.'
          : 'Impossible de lancer la dictée.')
        recording = false
        updateVoiceButton(micButton)
      }
      recognition.onend = function () {
        if (recording) {
          recording = false
          updateVoiceButton(micButton)
          setVoiceStatus('Dictée terminée. Tu peux relire et corriger ton texte.')
        }
      }
      try {
        recognition.start()
        recording = true
        updateVoiceButton(micButton)
        setVoiceStatus('Dictée en cours... Appuie de nouveau sur le micro pour arrêter.')
      } catch (_error) {
        recording = false
        updateVoiceButton(micButton)
        setVoiceStatus('Impossible de démarrer la dictée dans ce navigateur.')
      }
    })
  }

  function resolveVoiceFile() {
    var input = el.querySelector('[data-debrief-voice-file]')
    var file = input && input.files && input.files[0] ? input.files[0] : null
    if (!file) return null
    return {
      name: file.name || 'Fichier vocal',
      type: file.type || 'audio/*',
      size: typeof file.size === 'number' ? file.size : null
    }
  }
}

function findOption(ui, id) {
  return (ui.options || []).find(function (option) { return option.id === id }) || null
}

function renderDebriefAudioPopin() {
  return renderPopin({
    title: 'Fichier vocal',
    ariaLabel: 'Ajouter un fichier vocal',
    className: 'wc-debrief-audio-popin',
    dialogClassName: 'wc-debrief-audio-dialog',
    closeAttributes: 'data-popin-close data-debrief-audio-close',
    header: '<div class="wc-popin-title">Fichier vocal</div>',
    body:
      '<div class="wc-debrief-audio-body">' +
        '<label class="wc-debrief-audio-file">' +
          '<span class="wc-debrief-audio-file-icon">' + renderLucideIcon('paperclip') + '</span>' +
          '<span class="wc-debrief-audio-file-copy">' +
            '<span class="wc-debrief-audio-file-title">Choisir un fichier audio</span>' +
            '<span class="wc-debrief-audio-file-name" data-debrief-audio-file-name>Aucun fichier sélectionné</span>' +
          '</span>' +
          '<input type="file" accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm" data-debrief-audio-input>' +
        '</label>' +
        '<div class="wc-debrief-audio-note">Le traitement vocal sera ajouté prochainement.</div>' +
        '<div class="wc-debrief-audio-actions">' +
          '<button type="button" class="wc-debrief-audio-close-button" data-popin-close data-debrief-audio-close>Fermer</button>' +
        '</div>' +
      '</div>'
  })
}

function bindDebriefAudioPopin(el, handlers) {
  if (!el) return
  Array.prototype.slice.call(el.querySelectorAll('[data-debrief-audio-close]')).forEach(function (button) {
    button.addEventListener('click', function () {
      if (handlers && typeof handlers.onClose === 'function') handlers.onClose()
    })
  })

  var input = el.querySelector('[data-debrief-audio-input]')
  var fileName = el.querySelector('[data-debrief-audio-file-name]')
  if (input && fileName) {
    input.addEventListener('change', function () {
      var file = input.files && input.files[0]
      fileName.textContent = file && file.name ? file.name : 'Aucun fichier sélectionné'
    })
  }
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttributeValue(value) {
  return escapeHtml(value).replace(/`/g, '&#96;')
}

export {
  bindDebriefAudioPopin,
  bindDebriefMatterIngestionUi,
  normalizeDebriefMatterIngestionUi,
  renderDebriefAudioPopin,
  renderDebriefMatterIngestionUi
}
